import React, { useState } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import PaymentNoticeModal from './PaymentNoticeModal';

// Shown once after an admin turns a card back on, on the cardholder's
// next visit to any signed-in page. Mounted from AppTopBar rather than
// Dashboard so it reaches whichever page they land on first -- a
// non-admin login redirects to /add_profile_image, not the dashboard, so
// a Dashboard-only mount would miss the very first screen after signing
// in, which is the one occasion this is meant to catch.
//
// Reuses PaymentNoticeModal: a short block of text dismissed by a single
// button, no backdrop dismissal and no Escape, so acknowledgement is a
// deliberate act. Note its body renders inside a <p>, so the copy here
// has to stay inline content.
//
// Whether to show at all is decided server-side (see
// GET /api/auth/current's reactivationNotice, which compares the
// acknowledgement timestamp against reactivatedAt) -- never from
// localStorage, for the same reason paymentNoticeAckedAt is server-side:
// this is a property of the person, not of the browser they happen to be
// holding.
const formatTermDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
};

const ReactivationNotice = ({ user }) => {
  // Held locally once dismissed so the modal closes immediately without
  // waiting on the request or a refetch -- same shape as SocialEditor's
  // handling of the payment notice.
  const [dismissed, setDismissed] = useState(false);

  const notice = user && user.reactivationNotice;
  if (!notice || dismissed) return null;

  const acknowledge = () => {
    setDismissed(true);
    // Fire and forget: the modal has already closed, and a failure here
    // only means the notice appears once more on another device.
    axios
      .patch('/api/users/reactivation-notice/acknowledged')
      .catch((err) =>
        console.error(`Failed to acknowledge reactivation notice: ${err.message}`)
      );
  };

  // 'activated' when the card had never been activated before -- telling
  // someone their card was "reactivated" when it had never been active
  // would be plainly wrong to them.
  const isFirstActivation = notice.kind === 'activated';
  const endsOn = formatTermDate(notice.endsAt);
  const startedOn = formatTermDate(notice.startedAt);

  // States the term and its end date -- both read from the account's own
  // stored values, never a hardcoded number, because 3-year and 5-year
  // customers both exist and reactivation is what sets this one.
  //
  // Deliberately promises the term and nothing about the mechanism: no
  // reminder before it lapses, no automatic renewal, no claim that the
  // card switches itself off on that date. Expiry is not enforced
  // anywhere in the app today -- a card stays live until an admin turns
  // it off -- so the end date is a statement of the term the customer
  // holds, which is true, rather than a description of what the system
  // will do, which would not be.
  return (
    <PaymentNoticeModal
      title={isFirstActivation ? 'Your card is active' : 'Your card is active again'}
      buttonLabel="Got it"
      onAcknowledge={acknowledge}
    >
      {`Your Digital Contact Card has been ${
        isFirstActivation ? 'activated' : 'reactivated'
      }.`}
      {notice.validityYears && endsOn
        ? ` Your ${notice.validityYears}-year term began on ${startedOn} and runs through ${endsOn}.`
        : ''}
    </PaymentNoticeModal>
  );
};

const mapStateToProps = (state) => ({
  user: state.registerUser.user,
});

export default connect(mapStateToProps)(ReactivationNotice);
